import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

type Option<T> = { label: string; value: T };

interface Props<T> {
  value: T;
  onChange: (value: T) => void;
  options: Option<T>[];
}

export function CustomSelect<T extends string | number | boolean>({
  value,
  onChange,
  options,
}: Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <Wrapper ref={ref}>
      <Trigger type="button" onClick={() => setIsOpen((p) => !p)} $open={isOpen}>
        <span>{selected?.label ?? ""}</span>
        <Chevron $open={isOpen} viewBox="0 0 14 8" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1 1L7 7L13 1"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </Chevron>
      </Trigger>
      {isOpen && (
        <Dropdown>
          {options.map((opt) => (
            <Item
              key={String(opt.value)}
              $active={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
              {opt.value === value && (
                <Check viewBox="0 0 14 12" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M1 6L5.5 10.5L13 1"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </Check>
              )}
            </Item>
          ))}
        </Dropdown>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 8px;
`;

const Trigger = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 1.5px solid ${({ $open }) => ($open ? "var(--color-primary)" : "var(--color-border)")};
  border-radius: var(--radius-md);
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text);
  cursor: pointer;
  outline: none;
  transition: var(--transition);
  box-shadow: ${({ $open }) => ($open ? "0 0 0 4px var(--color-primary-soft)" : "none")};

  &:hover {
    border-color: ${({ $open }) => ($open ? "var(--color-primary)" : "var(--color-primary-light)")};
  }
`;

const Chevron = styled.svg<{ $open: boolean }>`
  width: 14px;
  height: 8px;
  color: var(--color-primary);
  transition: transform var(--transition);
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};
  flex-shrink: 0;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 20;
  padding: 6px;
  animation: slideDown 160ms ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Item = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 14px;
  font-size: 15px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active }) => ($active ? "var(--color-primary)" : "var(--color-text)")};
  background: ${({ $active }) => ($active ? "var(--color-primary-soft)" : "transparent")};
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--color-primary-soft);
    color: var(--color-primary);
  }
`;

const Check = styled.svg`
  width: 14px;
  height: 12px;
  color: var(--color-primary);
  flex-shrink: 0;
`;
